export type NameSortMode = "firstNameAsc" | "firstNameDesc" | "surnameAsc" | "surnameDesc";

export type NameSortSelectors<TData> = {
  getFirstName: (row: TData) => string | null | undefined;
  getSurname: (row: TData) => string | null | undefined;
};

type SortRow<TData> = {
  original: TData;
};

export const defaultNameSortMode: NameSortMode = "surnameAsc";

export const nameSortModeLabels: Record<NameSortMode, string> = {
  firstNameAsc: "First name A-Z",
  firstNameDesc: "First name Z-A",
  surnameAsc: "Last name A-Z",
  surnameDesc: "Last name Z-A",
};

export const nameSortModes: NameSortMode[] = ["firstNameAsc", "firstNameDesc", "surnameAsc", "surnameDesc"];

export function getNextNameSortMode(mode: NameSortMode): NameSortMode {
  const nextModeByCurrent: Record<NameSortMode, NameSortMode> = {
    firstNameAsc: "firstNameDesc",
    firstNameDesc: "surnameAsc",
    surnameAsc: "surnameDesc",
    surnameDesc: "firstNameAsc",
  };

  return nextModeByCurrent[mode];
}

export function isNameSortDescending(mode: NameSortMode) {
  return mode.endsWith("Desc");
}

export function getNameSortingState(mode: NameSortMode, columnId = "name") {
  return [{ id: columnId, desc: isNameSortDescending(mode) }];
}

function normaliseNamePart(value: string | null | undefined) {
  return (value ?? "").trim();
}

function compareNamePart(left: string | null | undefined, right: string | null | undefined) {
  return normaliseNamePart(left).localeCompare(normaliseNamePart(right), undefined, { sensitivity: "base" });
}

export function compareNames<TData>(
  left: TData,
  right: TData,
  mode: NameSortMode,
  selectors: NameSortSelectors<TData>,
) {
  const firstNameComparison = compareNamePart(selectors.getFirstName(left), selectors.getFirstName(right));
  const surnameComparison = compareNamePart(selectors.getSurname(left), selectors.getSurname(right));
  const comparison = mode.startsWith("firstName")
    ? firstNameComparison || surnameComparison
    : surnameComparison || firstNameComparison;

  return comparison;
}

export function createNameSortFn<TData>(mode: NameSortMode, selectors: NameSortSelectors<TData>) {
  return (rowA: SortRow<TData>, rowB: SortRow<TData>) => compareNames(rowA.original, rowB.original, mode, selectors);
}
